import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";

/**
 * WARSAW RADIO MAST (Konstantynów, Poland) — at 646.38 m the tallest structure
 * ever built until it collapsed on 8 August 1991 during a guy-wire exchange. A
 * triangular guyed steel lattice mast. Built from its published spec (Wikipedia /
 * construction records):
 *   - Height 646.38 m; 86 sections of 7.5 m; equilateral triangular cross section
 *     with a 4.8 m face → circumradius 4.8/√3 ≈ 2.77 m (constant up the mast).
 *   - Vertices: 245 mm steel tubes, wall 8–34 mm (thickest low down).
 *   - THREE guy arrays, each attached at FIVE levels: 121.78, 256.78, 369.28,
 *     481.78 and 594.28 m; each guy 50 mm dia. 80 t of guys + insulators.
 *
 * A 646 m guyed mast is far beyond anything the sim was tuned for and will very
 * likely sway/collapse — that's expected; accuracy to the real design is the goal
 * (physics is being tuned separately). Grade: mild structural steel (~St3S ≈ A36).
 */

// 245 mm OD tube, ~20 mm avg wall → A ≈ 0.0141 m², I ≈ 9.0e-5 m⁴.
const MAST_LEG: Section = { name: "Warsaw mast leg (245 mm tube)", A: 0.0141, I: 3e-3, dragWidth: 0.245 };
// 50 mm guy strand: A = π/4·0.05² ≈ 1.96e-3 m² (far bigger than the EHS catalog).
const GUY_50MM: Section = { name: "Warsaw 50 mm guy", A: 1.96e-3, I: 3e-7, dragWidth: 0.05 };

const H = 646.38;
// five guy levels; anchor radius grows with attachment height for sane guy angles
const guyLevels: Array<[number, number]> = [
  [121.78, 150],
  [256.78, 250],
  [369.28, 320],
  [481.78, 380],
  [594.28, 430],
];

export const design: TowerDesign = {
  id: "warsaw-radio-mast",
  name: "Warsaw Radio Mast (Konstantynów)",
  description: "646 m triangular guyed mast — tallest ever built; collapsed 1991.",
  author: "iconic tower — from published spec (Wikipedia / construction records)",
  spec: {
    type: "guyed",
    height: H,
    stack: {
      faces: 3, // equilateral triangular cross section
      panels: 172, // real sub-bracing pitch (~3.75 m), not one X per 7.5 m section
      baseRadius: 2.77, // 4.8 m face → circumradius 2.77 m
      topRadius: 2.77, // constant section up the mast
      legSection: MAST_LEG,
      braceSection: "angleL5", // real L5×5×3/8 diagonal lattice bracing
      legMaterial: "a36", // mild structural steel (≈ St3S)
      braceMaterial: "a36",
      bracing: "X",
    },
    guys: guyLevels.map(([h, anchorRadius]) => ({
      atHeightFrac: h / H,
      anchorRadius,
      section: GUY_50MM,
      material: "guyStrand",
      pretensionStrain: 0.0012,
    })),
    foundation: {
      soil: "stiff-clay", // Mazovian plain
      base: { type: "pad", width: 6, diameter: 1, depth: 4, concrete: "c20" }, // central pivot bearing block
      anchor: { type: "deadman", width: 6, diameter: 1, depth: 4, concrete: "c20" }, // large guy anchor blocks
    },
  },
};
